/**
 * The AffDice SuperAdmin, in the reader's own language — en, es and pt-BR.
 *
 * One catalogue, written as one file per area under messages/ so the screens
 * of an area and their words sit side by side, and merged here. Each area's
 * keys carry its own prefix (`ops.`, `aff.`, …); a test checks that no two
 * areas define the same key, since a spread would let the later one win
 * without a word.
 *
 * What stays out: anything the server wrote (its notes, its error messages),
 * statuses, codes and currencies — those are identifiers, and an identifier
 * put behind a translation lookup is one nobody can search for afterwards.
 *
 * `t()` is called while rendering, never at module scope: modules load before
 * main.tsx has read the browser's language, so a label translated on import
 * would always be English.
 */
import { type Params, type Translator, tabTranslator } from '@affdice/portal-kit';

import { AFFILIATES_MESSAGES } from './messages/affiliates.ts';
import { BRANDS_MESSAGES } from './messages/brands.ts';
import { EDITS_MESSAGES } from './messages/edits.ts';
import { ENTITIES_MESSAGES } from './messages/entities.ts';
import { INTEGRITY_MESSAGES } from './messages/integrity.ts';
import { OPERATORS_MESSAGES } from './messages/operators.ts';
import { OVERVIEW_MESSAGES } from './messages/overview.ts';
import { PLATFORM_MESSAGES } from './messages/platform.ts';
import { SHELL_MESSAGES } from './messages/shell.ts';
import { TOUR_MESSAGES } from './messages/tour.ts';
import { VIEW_MESSAGES } from './messages/view.ts';

/** Each area's catalogue, by name — what the duplicate-key test walks. */
export const AMS_CATALOGUES = {
  shell: SHELL_MESSAGES,
  overview: OVERVIEW_MESSAGES,
  operators: OPERATORS_MESSAGES,
  brands: BRANDS_MESSAGES,
  entities: ENTITIES_MESSAGES,
  edits: EDITS_MESSAGES,
  affiliates: AFFILIATES_MESSAGES,
  platform: PLATFORM_MESSAGES,
  integrity: INTEGRITY_MESSAGES,
  view: VIEW_MESSAGES,
  tour: TOUR_MESSAGES,
} as const;

export const AMS_MESSAGES = {
  ...SHELL_MESSAGES,
  ...OVERVIEW_MESSAGES,
  ...OPERATORS_MESSAGES,
  ...BRANDS_MESSAGES,
  ...ENTITIES_MESSAGES,
  ...EDITS_MESSAGES,
  ...AFFILIATES_MESSAGES,
  ...PLATFORM_MESSAGES,
  ...INTEGRITY_MESSAGES,
  ...VIEW_MESSAGES,
  ...TOUR_MESSAGES,
} as const;

export type AmsMessageKey = keyof typeof AMS_MESSAGES & string;

const cache = new Map<string, Translator<typeof AMS_MESSAGES>>();

/** One string, in whatever language this tab is reading. */
export function t(key: AmsMessageKey, params?: Params): string {
  return tabTranslator(AMS_MESSAGES, cache)(key, params);
}

/**
 * The singular or plural wording of a counted message, by the count.
 *
 * Keys come in pairs, `<key>.one` and `<key>.other`; the count is passed to
 * the message as `{count}`, already formatted by the caller when it needs
 * grouping.
 */
export function tCount(
  base: string,
  count: number,
  params: Params = {},
): string {
  const key = (count === 1 ? `${base}.one` : `${base}.other`) as AmsMessageKey;
  return t(key, { count, ...params });
}
